import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * Window size: number of slides rendered on each side of the current index.
 * E.g. WINDOW_SIZE=3 → renders current ± 3 = up to 7 slides in DOM at once.
 * All other slides are fully unmounted to free memory.
 */
const WINDOW_SIZE = 3;

/** Minimum horizontal swipe distance (px) to trigger navigation. */
const MIN_SWIPE_DISTANCE = 40;

/** Minimum swipe velocity (px/ms) to trigger navigation even on short swipes. */
const MIN_SWIPE_VELOCITY = 0.3;

/**
 * Ratio of horizontal to vertical movement required to classify a gesture as
 * a horizontal swipe. Prevents accidental navigation on diagonal scrolls.
 */
const SWIPE_DIRECTION_RATIO = 1.5;

/**
 * useCarousel
 *
 * Manages all carousel state and interaction for a windowed, high-performance carousel.
 *
 * @param {number} totalImages - Total number of images in the gallery
 * @param {number} initialIndex - Starting slide index (0-based)
 * @returns carousel controls and state
 */
export function useCarousel(totalImages, initialIndex = 0) {
  const [currentIndex, setCurrentIndex] = useState(() =>
    totalImages > 0 ? Math.min(Math.max(0, initialIndex), totalImages - 1) : 0
  );

  // Touch tracking refs (avoid re-renders during gesture)
  const touchRef = useRef({ startX: 0, startY: 0, startTime: 0, lastX: 0, lastY: 0 });
  const isSwiping = useRef(false);

  // Reset index when totalImages changes (e.g. carousel reopened with new images)
  useEffect(() => {
    setCurrentIndex(prev =>
      totalImages > 0 ? Math.min(prev, totalImages - 1) : 0
    );
  }, [totalImages]);

  // Reset to initialIndex when it changes (e.g. user clicks a different thumbnail)
  useEffect(() => {
    if (totalImages > 0) {
      setCurrentIndex(Math.min(Math.max(0, initialIndex), totalImages - 1));
    }
  }, [initialIndex, totalImages]);

  /** Navigate to a specific index (clamped) */
  const goTo = useCallback((index) => {
    if (totalImages === 0) return;
    setCurrentIndex(Math.min(Math.max(0, index), totalImages - 1));
  }, [totalImages]);

  /** Navigate to the next slide (wraps around) */
  const goNext = useCallback(() => {
    if (totalImages === 0) return;
    setCurrentIndex(prev => (prev < totalImages - 1 ? prev + 1 : 0));
  }, [totalImages]);

  /** Navigate to the previous slide (wraps around) */
  const goPrev = useCallback(() => {
    if (totalImages === 0) return;
    setCurrentIndex(prev => (prev > 0 ? prev - 1 : totalImages - 1));
  }, [totalImages]);

  /**
   * Compute the set of indices that should currently be rendered (mounted).
   * Indices outside this window are fully unmounted from the DOM.
   */
  const getWindowedIndices = useCallback((index, total) => {
    if (total === 0) return new Set();
    const indices = new Set();
    const start = Math.max(0, index - WINDOW_SIZE);
    const end = Math.min(total - 1, index + WINDOW_SIZE);
    for (let i = start; i <= end; i++) {
      indices.add(i);
    }
    return indices;
  }, []);

  const windowedIndices = getWindowedIndices(currentIndex, totalImages);

  // ─── Touch / Swipe Handlers ───────────────────────────────────────────────

  const handleTouchStart = useCallback((e) => {
    const touch = e.touches[0];
    touchRef.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      startTime: Date.now(),
      lastX: touch.clientX,
      lastY: touch.clientY,
    };
    isSwiping.current = false;
  }, []);

  const handleTouchMove = useCallback((e) => {
    const touch = e.touches[0];
    touchRef.current.lastX = touch.clientX;
    touchRef.current.lastY = touch.clientY;
    isSwiping.current = true;
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!isSwiping.current) return;
    const { startX, startY, startTime, lastX, lastY } = touchRef.current;
    const dx = startX - lastX;
    const dy = startY - lastY;
    const dt = Math.max(Date.now() - startTime, 1);
    const velocity = Math.abs(dx) / dt; // px/ms

    // Only handle clearly horizontal swipes (dx > dy * SWIPE_DIRECTION_RATIO) with sufficient distance or speed
    if (Math.abs(dx) > Math.abs(dy) * SWIPE_DIRECTION_RATIO && (Math.abs(dx) > MIN_SWIPE_DISTANCE || velocity > MIN_SWIPE_VELOCITY)) {
      if (dx > 0) goNext();
      else goPrev();
    }
    isSwiping.current = false;
  }, [goNext, goPrev]);

  // ─── Keyboard Handler ─────────────────────────────────────────────────────

  const handleKeyDown = useCallback((e) => {
    switch (e.key) {
      case 'ArrowRight':
        goNext();
        break;
      case 'ArrowLeft':
        goPrev();
        break;
      default:
        break;
    }
  }, [goNext, goPrev]);

  return {
    currentIndex,
    goTo,
    goNext,
    goPrev,
    windowedIndices,
    touchHandlers: {
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd,
    },
    handleKeyDown,
  };
}
